/**
 * Flow editor — floating "Minimap Options" window.
 *
 * A small draggable panel (opened with the ⚙ in the Minimap panel header) with
 * live sliders for the minimap camera's HEIGHT (Y), POSITION (forward/back along
 * the runway) and ROTATION (look-at depth → tilt), plus ZOOM (beat spacing —
 * spreads packed notes apart). Camera values live in the Flow store and are
 * applied by FlowMinimap's camera rig; zoom is an interface setting. Both persist.
 */

import React from 'react';
import FloatingWindow from './FloatingWindow';
import { useFlowStore } from './flowStore';
import { color, font } from '../../components/ui/theme';
import GradientSlider from '../../components/ui/GradientSlider';

type CamKey = 'height' | 'posZ' | 'tilt';

const RANGES: Record<CamKey, { min: number; max: number; step: number; label: string; hint: string }> = {
  height: { min: 2,   max: 14, step: 0.1, label: 'Height',   hint: 'Camera height (Y) — higher looks more top-down' },
  posZ:   { min: -4,  max: 8,  step: 0.1, label: 'Position', hint: 'Forward / back along the runway' },
  tilt:   { min: 10,  max: 90, step: 1,   label: 'Rotation', hint: 'Tilt angle (°) — higher looks more top-down' },
};

const ORDER: CamKey[] = ['height', 'posZ', 'tilt'];

/** Minimap zoom range (beat-spacing multiplier). */
const ZOOM_MIN = 0.5;
// 6× — reviewers wanted enough spread to open up packed sections (was 2×).
const ZOOM_MAX = 6;

export default function FlowMinimapCamWindow() {
  const open      = useFlowStore(s => s.minimapCamWinOpen);
  const setOpen   = useFlowStore(s => s.setMinimapCamWinOpen);
  const cam       = useFlowStore(s => s.minimapCam);
  const setCam    = useFlowStore(s => s.setMinimapCam);
  const resetCam  = useFlowStore(s => s.resetMinimapCam);
  // Zoom = minimap beat spacing (was in Settings → UI; it belongs next to the
  // camera controls it shares a panel with).
  const zoom      = useFlowStore(s => s.flowInterfaceSettings.minimapZoom) ?? 1;
  const setIface  = useFlowStore(s => s.setFlowInterfaceSettings);

  if (!open) return null;

  return (
    <FloatingWindow
      // New id (was "minimap-cam"): the panel gained the Zoom row, so it starts
      // from the fresh default rect instead of a saved one that's too short.
      id="minimap-options"
      title="Minimap Options"
      onClose={() => setOpen(false)}
      defaultRect={{ x: Math.max(8, window.innerWidth - 300), y: 130, w: 264, h: 348 }}
      minW={230}
      minH={280}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {ORDER.map(k => {
          const r = RANGES[k];
          const val = cam[k];
          return (
            <div key={k} style={rowStyle}>
              <GradientSlider
                inline
                label={r.label}
                value={(val - r.min) / (r.max - r.min)}
                onChange={(t) => {
                  const raw = r.min + t * (r.max - r.min);
                  const snapped = Math.round(raw / r.step) * r.step;
                  setCam({ [k]: Math.max(r.min, Math.min(r.max, parseFloat(snapped.toFixed(4)))) });
                }}
                valueText={r.step < 1 ? val.toFixed(1) : val.toFixed(0)}
              />
              <div style={hintStyle}>{r.hint}</div>
            </div>
          );
        })}

        <div style={rowStyle}>
          <GradientSlider
            inline
            label="Zoom"
            value={(zoom - ZOOM_MIN) / (ZOOM_MAX - ZOOM_MIN)}
            onChange={(t) => {
              const raw = ZOOM_MIN + t * (ZOOM_MAX - ZOOM_MIN);
              const snapped = Math.round(raw / 0.05) * 0.05;
              setIface({ minimapZoom: Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, parseFloat(snapped.toFixed(2)))) });
            }}
            valueText={`${zoom.toFixed(2)}×`}
          />
          <div style={hintStyle}>Beat spacing — turn it up so packed sections stop looking like a solid bar</div>
        </div>

        <button
          type="button"
          onClick={() => { resetCam(); setIface({ minimapZoom: 1 }); }}
          style={resetBtnStyle}
        >
          Reset to default
        </button>
      </div>
    </FloatingWindow>
  );
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const rowHeadStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: color.accentSoft,
  fontFamily: font.sans,
};

const valueStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  fontFamily: 'monospace',
  color: color.inkDim,
};

const rangeStyle: React.CSSProperties = {
  width: '100%',
  // Themes the native range track + thumb to the accent in Chromium/Firefox.
  accentColor: 'var(--accent)',
  cursor: 'pointer',
};

const hintStyle: React.CSSProperties = {
  fontSize: 9,
  lineHeight: 1.4,
  color: color.inkFaint,
  fontFamily: font.sans,
};

const resetBtnStyle: React.CSSProperties = {
  marginTop: 2,
  padding: '8px 12px',
  borderRadius: 999,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: 0.5,
  cursor: 'pointer',
  fontFamily: font.sans,
  color: color.accentSoft,
  background: 'oklch(0.20 calc(0.028 * var(--tint-c)) var(--tint-h) / 0.6)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.3)',
};
